import {
    Autocomplete,
    Avatar,
    Box,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    TextField,
    Typography,
    IconButton,
    Divider,
    Stack,
    Chip,
    Tooltip,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { FaCartShopping, FaMagnifyingGlass, FaPen, FaRegImage, FaTrash } from "react-icons/fa6";
import { getProducts, getProductDetails } from "../../../helpers/http_products_requests";
import { formatCurrency, formatOptionsFromApi, mapExtraServices } from "../../../utils/functions";
import { useModal } from "../../../providers/ModalProvider";
import { getCategories } from "../../../helpers/http_categories_requests";
import { getFormats } from "../../../helpers/http_formats_requests";
import { getComforts } from "../../../helpers/http_comforts_requests";
import { getPlatings } from "../../../helpers/http_platings_requests";
import { getExternalFinishings } from "../../../helpers/http_external_finishings_requests";
import { getServiceDetails, getServices } from "../../../helpers/http_services_requests";
import { ringSizeArray } from "../../../utils/data_variables";
import { useCatalog } from "../../../providers/CatalogProvider";
import { getStoneVariations } from "../../../helpers/http_stone_variations_requests";
import { getStones } from "../../../helpers/http_stones_requests";

export default function ProductManagement({ isCustomerDefined, currentTotalPrice = "000", handleIncludeProduct, handleRemoveProduct, productList }) {

    const { setModalTitle, handleOpen, setFormFieldList, setInitialData, setIsDetails, setMultiImageUpload, setOnSubmitHandler } = useModal();
    const { materials, metalList } = useCatalog();

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 5 });
    const formatsQuery = useQuery({ queryKey: ['getFormats'], queryFn: getFormats, staleTime: 1000 * 5 });
    const comfortsQuery = useQuery({ queryKey: ['getComforts'], queryFn: getComforts, staleTime: 1000 * 5 });
    const platingsQuery = useQuery({ queryKey: ['getPlatings'], queryFn: getPlatings, staleTime: 1000 * 5 });
    const externalFinishingsQuery = useQuery({ queryKey: ['getExternalFinishings'], queryFn: getExternalFinishings, staleTime: 1000 * 5 });

    const productsQuery = useQuery({ queryKey: ['getProducts'], queryFn: getProducts, staleTime: 1000 * 15 });
    const servicesQuery = useQuery({ queryKey: ['getServices'], queryFn: getServices, staleTime: 1000 * 15 });
    const stonesQuery = useQuery({ queryKey: ['getStones'], queryFn: getStones, staleTime: 1000 * 5 });

    const queries = [categoriesQuery, formatsQuery, comfortsQuery, platingsQuery, externalFinishingsQuery, productsQuery, servicesQuery, stonesQuery];
    const isFetching = queries.some(query => query.isFetching);

    const categories = formatOptionsFromApi(categoriesQuery);
    const formats = formatOptionsFromApi(formatsQuery);
    const comforts = formatOptionsFromApi(comfortsQuery);
    const platings = formatOptionsFromApi(platingsQuery);
    const externalFinishings = formatOptionsFromApi(externalFinishingsQuery);
    const stones = formatOptionsFromApi(stonesQuery);

    const [fieldValue, setFieldValue] = useState('');

    const { data: services } = servicesQuery?.data?.data ?? {};

    const currentServices = services?.map((obj) => ({
        value: obj.id,
        label: `${obj.name} (${formatCurrency(obj.price)})`,
    }));

    const currentExtraServices = mapExtraServices(services);

    const productFormFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true },
                { name: "category", label: "Categoria", type: "select", required: true, options: categories },
                { name: "selected_metal", label: "Material", type: "select", required: true, options: metalList },
                { name: "format", label: "Formato", type: "select", options: formats },
                { name: "confort", label: "Conforto", type: "select", options: comforts },
                { name: "height", label: "Altura", type: "number", end_adornment: "mm" },
                { name: "width", label: "Largura", type: "number", end_adornment: "mm" },
                { name: "thickness", label: "Espessura", type: "number", end_adornment: "mm" },
                { name: "plating", label: "Banho", type: "select", options: platings },
                { name: "finishing", label: "Acabamento Externo", type: "select", options: externalFinishings },
                { name: "is_crafted", label: "Joia trabalhada / montada", type: "check" },
                { name: "public_description", label: "Descrição Pública", type: "text", multiline: true, rows: 5, fullWidth: true },
            ]
        },
        {
            title: "Tamanhos e Gravações",
            fields: [
                { name: "size_one", label: "Tamanho joia 1", type: "select", options: ringSizeArray },
                { name: "engraving_one", label: "Gravação joia 1", type: "text", },
                { name: "size_two", label: "Tamanho joia 2", type: "select", options: ringSizeArray },
                { name: "engraving_two", label: "Gravação joia 2", type: "text", },
            ]
        },
        {
            title: "Pesos de Produto",
            fields: [
                { name: "high_gold_weight", conditional: { field: 'selected_metal', value: 1 }, label: "Peso em Ouro 18K", type: "number", required: true, end_adornment: "g" },
                { name: "low_gold_weight", conditional: { field: 'selected_metal', value: 2 }, label: "Peso em Ouro 10K", type: "number", required: true, end_adornment: "g" },
                { name: "silver_weight", conditional: { field: 'selected_metal', value: 3 }, label: "Peso em Prata 950", type: "number", required: true, end_adornment: "g" },
            ]
        },
        {
            title: "Pedras de Produto",
            fields: [
                {
                    name: "stones",
                    label: "Pedras",
                    type: "stones",
                    fullWidth: true,
                    modal_props: {
                        title: 'Inclusão de Pedras',
                        fields: [
                            {
                                title: 'Especificações',
                                fields: [
                                    {
                                        name: 'stone',
                                        label: 'Pedra',
                                        type: 'select_object',
                                        required: true,
                                        options: stones,
                                    },
                                    {
                                        name: 'variation',
                                        label: 'Variação',
                                        type: 'select_object',
                                        required: true,
                                        dependsOn: 'stone',
                                        dynamicOptions: (stoneId) => getStoneVariations(stoneId)
                                    },
                                    {
                                        name: 'quantity',
                                        label: 'Quantidade',
                                        type: 'number',
                                        required: true,
                                        end_adornment: 'unidades',
                                    },
                                    {
                                        name: "observation",
                                        label: "Observação",
                                        type: "text",
                                        multiline: true,
                                        rows: 5,
                                        fullWidth: true,
                                    }

                                ]
                            }
                        ]
                    }
                },
            ]
        },
        {
            title: "Serviços e Extras",
            fields: [
                {
                    type: 'extras',
                    name: 'extras',
                    label: 'Serviços Extras',
                    options: currentExtraServices || [],
                    fullWidth: true,
                    getOptionLabel: (option) => `${option.label} (${formatCurrency(option.price)})`
                }
            ]
        },
        {
            title: "Ficha Técnica",
            fields: [
                { name: "self_reference", label: "Referência Interna", type: "text", required: true },
                { name: "prod_time", label: "Tempo Ideal de Produção", type: "number", required: true, end_adornment: "dias" },
                { name: "hard_weight_high_gold", conditional: { field: 'selected_metal', value: 1 }, label: "Peso Bruto Ouro 18K", type: "number", required: true, end_adornment: "g" },
                { name: "hard_weight_low_gold", conditional: { field: 'selected_metal', value: 2 }, label: "Peso Bruto Ouro 10K", type: "number", required: true, end_adornment: "g" },
                { name: "hard_weight_silver", conditional: { field: 'selected_metal', value: 3 }, label: "Peso Bruto Prata 950", type: "number", required: true, end_adornment: "g", max_length: 1 },
                { name: "production_description", label: "Descrição para Produção", type: "text", multiline: true, rows: 5, fullWidth: true, required: true },
                { name: "prod_steps_json", label: "Passos de Produção", type: "steps", fullWidth: true },
            ]
        },
        {
            title: "Data de Entrega",
            fields: [
                { name: "delivery_date", label: "Data de Entrega", type: "date_time_picker", required: true },
            ]
        },

    ];

    const serviceFormFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true, disabled: true },
                { name: "selected_metal", label: "Material", type: "select", required: true, options: metalList },
            ]
        },
        {
            title: "Ficha Técnica",
            fields: [
                { name: "self_reference", label: "Referência Interna", type: "text", required: true },
                { name: "prod_time", label: "Tempo Ideal de Produção", type: "number", required: true, end_adornment: "dias" },
                { name: "production_description", label: "Descrição para Produção", type: "text", multiline: true, rows: 5, fullWidth: true },
                { name: "prod_steps_json", label: "Passos de Produção", type: "steps", fullWidth: true },
            ]
        },
        {
            title: "Data de Entrega",
            fields: [
                { name: "delivery_date", label: "Data de Entrega", type: "date_time_picker", required: true },
            ]
        },

    ];

    const { products = [] } = productsQuery?.data?.data ?? {};

    const currentProducts = products?.map((obj) => ({
        value: obj.id,
        label: `${obj.name} (${obj.self_reference})`,
    }));

    const handleOpenProductDetails = (value) => {
        setFormFieldList(productFormFieldList);
        setModalTitle("Detalhes de Produto");
        setInitialData(value);
        setIsDetails(true);
        setMultiImageUpload(true);
        handleOpen();
    };

    const handleUpdateProduct = (data) => {
        handleRemoveProduct(data.index);
        handleIncludeProduct(data);
    };

    const handleOpenProductUpdate = (value, index) => {
        setFormFieldList(productFormFieldList);
        setModalTitle("Edição de Produto");
        setInitialData({ ...value, index: index });
        setMultiImageUpload(true);
        setOnSubmitHandler(() => handleUpdateProduct);
        handleOpen();
    };

    const handleOpenProductRegister = (value) => {
        setFormFieldList(productFormFieldList);
        setModalTitle("Inclusão de Produto");
        setInitialData(value);
        setMultiImageUpload(true);
        setOnSubmitHandler(() => handleIncludeProduct);
        handleOpen();
    };

    const handleOpenServiceUpdate = (value, index) => {
        setFormFieldList(serviceFormFieldList);
        setModalTitle("Edição de Serviço");
        setInitialData({ ...value, index: index });
        setMultiImageUpload(true);
        setOnSubmitHandler(() => handleUpdateProduct);
        handleOpen();
    };

    const handleOpenServiceRegister = (value) => {
        setFormFieldList(serviceFormFieldList);
        setModalTitle("Inclusão de Serviço");
        setInitialData({ ...value, is_service: true });
        setMultiImageUpload(true);
        setOnSubmitHandler(() => handleIncludeProduct);
        handleOpen();
    };

    const handleOpenServiceDetails = (value) => {
        setFormFieldList(serviceFormFieldList);
        setModalTitle("Detalhes de Serviço");
        setInitialData({ ...value, is_service: true });
        setIsDetails(true);
        setMultiImageUpload(true);
        handleOpen();
    };

    const handleProductChange = async (_, value) => {
        if (value) {
            const productDetails = await getProductDetails(value.value);
            handleOpenProductRegister(productDetails?.data?.data);
            setFieldValue('');
        };
    };

    const handleServiceChange = async (_, value) => {

        const serviceDetails = await getServiceDetails(value.value);

        const servicePrice = serviceDetails.data.data.price;

        if (value) {
            handleOpenServiceRegister({
                name: value.label,
                self_reference: 'SERVIÇO',
                price: servicePrice
            });
            setFieldValue('');
        };
    };

    return (
        <Box sx={styles.box}>
            <Box sx={styles.header}>
                <Box sx={styles.headerContainer}>
                    <Box component={FaCartShopping} fontSize={'1.25rem'} />
                    <Typography sx={styles.headerText}>Carrinho</Typography>
                </Box>
                <Tooltip title={!isCustomerDefined && "Selecione um cliente para continuar"} arrow>
                    <Autocomplete
                        disabled={isFetching || !isCustomerDefined}
                        value={fieldValue}
                        disablePortal
                        fullWidth
                        size="small"
                        onChange={handleProductChange}
                        options={currentProducts}
                        sx={styles.autocomplete}
                        renderInput={(params) => (
                            <TextField
                                size="small"
                                {...params}
                                label="Produto"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FaMagnifyingGlass />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Tooltip>
                <Tooltip title={!isCustomerDefined && "Selecione um cliente para continuar"} arrow>
                    <Autocomplete
                        disabled={isFetching || !isCustomerDefined}
                        value={fieldValue}
                        disablePortal
                        fullWidth
                        size="small"
                        onChange={handleServiceChange}
                        options={currentServices}
                        sx={styles.autocomplete}
                        renderInput={(params) => (
                            <TextField
                                size="small"
                                {...params}
                                label="Serviço"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FaMagnifyingGlass />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                </Tooltip>
            </Box>
            <Box sx={styles.list}>
                <List sx={styles.listContainer}>
                    {productList?.map((product, index) => {

                        const extras = product.extras;

                        const stones = product.stones;

                        var extraPrice = 0;

                        var stonePrice = 0;

                        if (extras?.length > 0) {

                            extras.forEach(extra => {
                                extraPrice += extra.price;

                            });

                        };

                        if (stones?.length > 0) {
                            stones.forEach(stone => {

                                var quantity = Number(stone.quantity);
                                var unitPrice = Number(stone.price);

                                var stoneAmountPrice = unitPrice * quantity;

                                stonePrice += stoneAmountPrice;
                            });
                        };

                        const productPrice = formatCurrency(product?.price + extraPrice + stonePrice);
                        return (
                            <Fragment key={index}>
                                <ListItem sx={styles.listItem}>
                                    <ListItemAvatar>
                                        <Avatar alt={product?.complete_name} src={product?.images?.[0]}>
                                            <FaRegImage />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <Box sx={styles.listItemContent}>
                                        <Box sx={styles.productDetails}>
                                            <Typography fontWeight={600}>{product?.name} ({product?.self_reference})</Typography>
                                            {
                                                product.public_description && (
                                                    <Typography variant="subtitle2" sx={styles.productDescription}>
                                                        {product?.public_description.slice(0, 40) + '...' || "Produto sem descrição"}
                                                    </Typography>
                                                )
                                            }
                                            <Stack alignItems={'start'} mt={'1rem'} spacing={1}>
                                                {(product?.extras || []).map((obj, index) => (
                                                    <Chip key={index} label={`${obj.label} (${formatCurrency(obj.price)})`} />
                                                ))}
                                            </Stack>
                                            <Stack mt={'1rem'} alignItems={'end'} direction={"row"} spacing={1}>
                                                {product?.selected_metal && materials[product?.selected_metal] && <Chip sx={{ bgcolor: materials[product?.selected_metal].color }} label={materials[product?.selected_metal].name} />}
                                                {product?.is_crafted == 1 && <Chip color='success' label="JOIA TRABALHADA" />}
                                            </Stack>
                                        </Box>
                                        <Box sx={styles.productActions}>
                                            <Stack direction={"row"} spacing={1}>
                                                <IconButton
                                                    size="small"
                                                    sx={styles.deleteButton}
                                                    onClick={() =>
                                                        product.is_service
                                                            ? handleOpenServiceDetails(product)
                                                            : handleOpenProductDetails(product)
                                                    }
                                                >
                                                    <Box component={FaMagnifyingGlass} color={'black'} />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    sx={styles.deleteButton}
                                                    onClick={() =>
                                                        product.is_service
                                                            ? handleOpenServiceUpdate(product, index)
                                                            : handleOpenProductUpdate(product, index)
                                                    }
                                                >
                                                    <Box component={FaPen} color={'black'} />
                                                </IconButton>
                                                <IconButton size="small" sx={styles.deleteButton} onClick={() => handleRemoveProduct(index)}>
                                                    <Box component={FaTrash} color={'red'} />
                                                </IconButton>
                                            </Stack>
                                            <Typography>{productPrice}</Typography>
                                        </Box>
                                    </Box>
                                </ListItem>
                                <Divider />
                            </Fragment>
                        )
                    })}
                </List>
            </Box>
            <Box sx={styles.subtotalContainer}>
                <Typography sx={styles.subtotalText}>Subtotal</Typography>
                <Typography>{formatCurrency(currentTotalPrice)}</Typography>
            </Box>
        </Box>
    );
}

const styles = {
    box: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'start',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        padding: 2,
        boxSizing: "border-box",
    },
    header: {
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        flexDirection: 'column',
        width: '100%',
        height: '35%',
    },
    headerContainer: {
        display: 'flex',
        justifyContent: 'start',
        alignItems: 'center',
        width: '100%'
    },
    headerText: {
        ml: '.5rem',
        fontSize: '1.25rem',
        textAlign: 'left',
    },
    autocomplete: {
        width: '100%',
        my: '1rem',
    },
    list: {
        overflow: 'auto',
        height: '40vh',
        width: '100%',
    },
    listContainer: {
        maxHeight: '100%',
    },
    listItem: {
        p: '1rem',
        display: 'flex',
        alignItems: 'flex-start',
    },
    listItemContent: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
    },
    productDetails: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'left',
    },
    productDescription: {
        textAlign: 'left',
        mt: '.5rem',
    },
    productActions: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
    },
    deleteButton: {
        color: 'red',
    },
    subtotalContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '15%',
    },
    subtotalText: {
        ml: '.5rem',
        fontSize: '1.25rem',
        textAlign: 'left',
    },
};